import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { CompanyService, PermissionSetName, UserTypeEnum } from '@cai-services';
import { datadogRum } from '@datadog/browser-rum';
import { ActivatedRoute } from '@angular/router';
import {
  ApplicationProperties,
  Office,
  RegisterService,
  SessionService,
  User,
  UserFeaturesEnum,
} from '../../core';
import { BrandIconUtil, HomepageCommonUtil, OfficeUtil } from '../../utils';
import { APP_PROPS } from '../../cai-common.module';
import { environment } from '../../environments';
import { NavGroup } from '../layout/settings/settings-layout/settings-layout.component';

@Component({
  selector: 'cai-office-settings',
  templateUrl: './office-settings.component.html',
  styleUrls: ['./office-settings.component.scss'],
  standalone: false,
})
export class OfficeSettingsComponent implements OnInit {
  activeTab:
    | 'office-settings'
    | 'office-management'
    | 'office-customers'
    | 'office-charges'
    | 'office-terms-and-conditions'
    | 'office-rate-markup';
  activeSubTab = '';
  navItems: NavGroup[] = [];

  features = environment.features;
  companyOffices: Office[] = [];
  activeOffice?: Office;
  currentUser: User;
  iAmAdmin = false;
  isForwarder = false;
  getOfficeName = OfficeUtil.getOfficeName;
  shouldOpenViewPlans = false;
  shouldOpenUpgradePlans = false;

  constructor(
    @Inject(APP_PROPS)
    private readonly appProperties: ApplicationProperties,
    private readonly cdr: ChangeDetectorRef,
    private readonly companyService: CompanyService,
    private readonly sessionService: SessionService,
    private readonly registerService: RegisterService,
    private readonly route: ActivatedRoute,
    public titleService: Title,
  ) {
    this.titleService.setTitle(
      `Office Settings - ${BrandIconUtil.fetchDomainTitle()}`,
    );
    this.route.queryParamMap.subscribe((paramMap) => {
      if (paramMap.get('upgradeSuccess') != null) {
        this.activeTab = 'office-management';
        this.cdr.detectChanges();
      }
    });
    this.isForwarder =
      this.sessionService.getCurrentUser().type === UserTypeEnum.FORWARDER;
  }

  async ngOnInit() {
    this.currentUser = await this.registerService.getUser();
    this.companyOffices = this.currentUser.officeAccesses.map(
      (officeAccess) => officeAccess.office as Office,
    );

    this.companyOffices = this.companyOffices.sort((a, b) =>
      OfficeUtil.sortCompare2(
        this.getOfficeName(a),
        this.getOfficeName(b),
        true,
        a.id,
        b.id,
        true,
      ),
    ) as Office[];

    if (!this.activeOffice) {
      this.activeOffice =
        this.sessionService.getSelectedOffice() != null
          ? this.sessionService.getSelectedOffice()
          : this.companyOffices[0];
    }
    const fragment = this.route.snapshot.fragment;

    const fragmentToTabMap: {
      [key: string]:
        | 'office-settings'
        | 'office-management'
        | 'office-charges'
        | 'office-rate-markup'
        | 'office-terms-and-conditions';
    } = {
      markupsettings: 'office-rate-markup',
      upgradeplans: 'office-management',
      account: 'office-settings',
      management: 'office-management',
      charges: 'office-charges',
      't&cs': 'office-terms-and-conditions',
      markup: 'office-rate-markup',
      viewplans: 'office-management',
    };

    this.activeTab = fragmentToTabMap[fragment] || 'office-settings';
    if (fragment === 'upgradeplans' || fragment === 'viewplans') {
      this.activeOffice = this.companyOffices.find(
        (office) => office.plan === 'FREE' || office.plan === 'TRIAL',
      );

      if (this.activeOffice) {
        if (fragment === 'upgradeplans') {
          this.shouldOpenUpgradePlans = true;
        } else if (fragment === 'viewplans') {
          this.shouldOpenViewPlans = true;
        }
      } else {
        this.activeOffice = this.companyOffices[0];
      }
    } else {
      this.activeOffice =
        this.sessionService.getSelectedOffice() || this.companyOffices[0];
    }

    await this.fetchOfficesUserIsAdminOf();
    this.initializeNavItems();
    this.cdr.detectChanges();
  }

  private initializeNavItems(): void {
    const officeNavSubItems = this.companyOffices.map((office) => ({
        key: office.id.toString(),
        label: this.getOfficeName(office),
      })),
      officesWithQuotation = this.companyOffices
        .filter((office) =>
          HomepageCommonUtil.isFeatureEnabled(
            office.activatedFeatures,
            UserFeaturesEnum.QUOTATION,
          ),
        )
        .map((office) => ({
          key: office.id.toString(),
          label: this.getOfficeName(office),
        })),
      officeNavItems = [
        {
          key: 'office-management',
          label: 'Management',
          iconUrl: '/assets/media/icons/settings-purple.svg',
          subItems: officeNavSubItems,
        },
      ];

    if (
      !this.isWalletApp &&
      this.isForwarder &&
      officesWithQuotation.length > 0
    ) {
      officeNavItems.push(
        {
          key: 'office-charges',
          label: 'Charges',
          iconUrl: '/assets/media/icons/settings-purple.svg',
          subItems: officesWithQuotation,
        },
        {
          key: 'office-terms-and-conditions',
          label: 'T&Cs',
          iconUrl: '/assets/media/icons/settings-purple.svg',
          subItems: officesWithQuotation,
        },
      );
    }

    if (!this.isWalletApp && this.isForwarder) {
      officeNavItems.push({
        key: 'office-rate-markup',
        label: 'Markup',
        iconUrl: '/assets/media/icons/settings-purple.svg',
        subItems: officesWithQuotation,
      });
    }

    this.navItems = [
      {
        groupName: 'COMPANY',
        groupItems: [
          {
            key: 'office-settings',
            label: 'Account management',
            iconUrl: '/assets/media/icons/home-purple.svg',
          },
        ],
      },
      {
        groupName: 'OFFICE',
        groupItems: officeNavItems,
      },
    ];

    this.onSelectNavSubItem(this.activeOffice.id.toString());
  }

  async fetchOfficesUserIsAdminOf(): Promise<void> {
    this.iAmAdmin = !!this.currentUser.officeAccesses?.find(
      (oa) =>
        oa.office.id === this.activeOffice.id &&
        oa.roles.find((p) => p.name === PermissionSetName.OFFICE_ADMIN),
    );
    this.cdr.detectChanges();
  }

  updateOffice(updatedActiveOffice: Office) {
    Object.assign(
      this.companyOffices.find(
        (office) => office.id === updatedActiveOffice.id,
      ),
      updatedActiveOffice,
    );
  }

  onDisplayedViewPlans(displayedViewPlans: boolean) {
    this.shouldOpenViewPlans = displayedViewPlans;
  }

  get isWalletApp(): boolean {
    return this.appProperties.name === 'cargowallet';
  }

  onSelectNavItem(navItemKey: string): void {
    this.activeTab = navItemKey as any;
  }

  onSelectNavSubItem(subItemKey: string): void {
    this.activeSubTab = subItemKey;
    const officeId = Number(subItemKey);
    this.activeOffice = this.companyOffices.find(
      (office) => office.id === officeId,
    );
    this.cdr.detectChanges();
  }
}
